import BillingsPlanEntryTaxiDriverAddParams, {
  BillingsPlanEntryTaxiDriverAddParamsBillingNode,
  BillingsPlanEntryTaxiDriverAddParamsBonusContribution,
  BillingsPlanEntryTaxiDriverAddParamsContractCorrectionContribution,
  BillingsPlanEntryTaxiDriverAddParamsContribution,
  BillingsPlanEntryTaxiDriverAddParamsContributionType,
  BillingsPlanEntryTaxiDriverAddParamsPenaltyContribution,
} from "../../../../../common/services/billings/plan-entry/taxi-driver/add/billings-plan-entry-taxi-driver-add-params";
import BillingsNode from "../../../common/types/billings-node";
import BillingsTaxiDriverContributionBonus, {
  BillingsTaxiDriverContributionBonusType,
} from "../../common/contributions/bonus/types/billings-taxi-driver-contributions-bonus";
import BillingsTaxiDriverContributionContractCorrection, {
  BillingsTaxiDriverContributionContractCorrectionType,
} from "../../common/contributions/contract-correction/types/billings-taxi-driver-contributions-contract-correction";
import BillingsTaxiDriverContributionPenalty, {
  BillingsTaxiDriverContributionPenaltyType,
} from "../../common/contributions/penalty/types/billings-taxi-driver-contributions-penalty";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";

const createBillingBonusContribution = (
  contribution: BillingsTaxiDriverContributionBonus,
): BillingsPlanEntryTaxiDriverAddParamsBonusContribution | undefined => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionBonusType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment ?? "",
        distance: contribution.distance ?? 0,
        rate: contribution.rate ?? 0,
      };
    case BillingsTaxiDriverContributionBonusType.BONUS_OTHER:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.BONUS_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    default:
      return undefined;
  }
};

const createBillingPenaltyContribution = (
  contribution: BillingsTaxiDriverContributionPenalty,
): BillingsPlanEntryTaxiDriverAddParamsPenaltyContribution | undefined => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_BEING_LATE:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.PENALTY_BEING_LATE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_INCOMPATIBLE_CAR:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.PENALTY_INCOMPATIBLE_CAR,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_OTHER:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.PENALTY_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_WRONG_ROUTE:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.PENALTY_WRONG_ROUTE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingContractCorrectionContribution = (
  contribution: BillingsTaxiDriverContributionContractCorrection,
):
  | BillingsPlanEntryTaxiDriverAddParamsContractCorrectionContribution
  | undefined => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionContractCorrectionType.BASE_AMOUNT_EXTERNAL_TAXI:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.BASE_AMOUNT_EXTERNAL_TAXI,
        distance: contribution.distance ?? 0,
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionContractCorrectionType.CONTRACT_CORRECTION_RATE:
      return {
        type: BillingsPlanEntryTaxiDriverAddParamsContributionType.CONTRACT_CORRECTION_RATE,
        rate: contribution.rate ?? 0,
      };
    default:
      return undefined;
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
): BillingsPlanEntryTaxiDriverAddParamsContribution[] => {
  const bonusContributions: BillingsPlanEntryTaxiDriverAddParamsBonusContribution[] =
    contributions.bonus.map(
      (contribution) => createBillingBonusContribution(contribution)!,
    );

  const penaltyContributions: BillingsPlanEntryTaxiDriverAddParamsPenaltyContribution[] =
    contributions.penalty.map(
      (contribution) => createBillingPenaltyContribution(contribution)!,
    );

  const contractCorrectionContributions: BillingsPlanEntryTaxiDriverAddParamsContractCorrectionContribution[] =
    contributions.contractCorrection.map(
      (contribution) =>
        createBillingContractCorrectionContribution(contribution)!,
    );

  return [
    ...bonusContributions,
    ...penaltyContributions,
    ...contractCorrectionContributions,
  ];
};

const createSaveBillingsParamsBillingNode = (
  node: BillingsNode,
): BillingsPlanEntryTaxiDriverAddParamsBillingNode => {
  return {
    allowCharge: node.isHighwayAllowed,
    checkoutDate: node.checkoutDate,
    checkoutEventId: node.checkoutEventUuid,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.isEditable,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    id: node.id,
    lat: node.lat,
    lon: node.lon,
    nodeId: node.nodeId,
    orderId: node.orderId,
    plannedDate: node.plannedDate,
    plannedDistance: node.plannedDistance,
  };
};

const createSaveBillingsParamsBillingNodes = (
  nodes: BillingsNode[],
): BillingsPlanEntryTaxiDriverAddParamsBillingNode[] => {
  return nodes.map(createSaveBillingsParamsBillingNode);
};

const createSaveBillingParams = (
  data: BillingFormData,
  planEntryId: string,
): BillingsPlanEntryTaxiDriverAddParams => {
  const result: BillingsPlanEntryTaxiDriverAddParams = {
    billingNodes: createSaveBillingsParamsBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
    planEntryId,
  };
  return result;
};

const billingSaveRequestFactory = {
  createSaveBillingParams,
};

export default billingSaveRequestFactory;
